import i18n, { InitOptions } from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import LocizeBackend from 'i18next-locize-backend';
import LastUsed from 'locize-lastused';
import opConfig from 'openpathConfig';

import { isDevelopmentEnv } from 'constants/envs';

import { locizeOptions } from './locize';
import { shouldSaveMissingToProduction } from './saveMissingEnvConfig';

/**
 * This function allows for setting up additional custom formatters to be used in translated strings.
 *
 * It must only be called AFTER i18next.init().
 *
 * See: https://www.i18next.com/translation-function/formatting
 *
 * @param {import('i18next').i18n} i18next The initialized i18next instance
 */
export const i18nPostInit: (services: typeof i18n) => void = ({ services }) => {
  services.formatter?.addCached('numericList', (lng, options) => {
    const listFormatter = new Intl.ListFormat(lng, {
      ...options.listOptions,
    });

    if (options.raw) {
      return (val) => listFormatter.format(val.map(String));
    }

    const numberFormatter = new Intl.NumberFormat(lng, {
      ...options.numberOptions,
    });

    const formattedNumber: (item: number) => string = (item) =>
      numberFormatter.format(item);

    return (val) => listFormatter.format(val.map(formattedNumber));
  });
};

const isDevelopmentEnvironment = isDevelopmentEnv(opConfig.ENV);

// Keep saveMissing gating aligned with locize.js so writes happen
// only in the allowlisted environments.
const shouldSaveMissing = shouldSaveMissingToProduction(opConfig.ENV);

export const i18nInitOptions: InitOptions = {
  fallbackLng: 'en',
  supportedLngs: ['en', 'fr', 'es', 'it', 'de', 'pt-BR', 'ja'],
  ns: ['translation'],
  defaultNS: 'translation',
  keySeparator: false,
  nsSeparator: false,
  interpolation: {
    escapeValue: false,
  },
  react: {
    transKeepBasicHtmlNodesFor: [
      'br',
      'strong',
      'i',
      'p',
      'div',
      'a',
      'em',
      'span',
      'b',
      'ul',
      'li',
    ],
    useSuspense: false,
  },
  nonExplicitSupportedLngs: false,
  load: 'currentOnly',
  saveMissing: shouldSaveMissing,
  saveMissingPlurals: true,
  backend: locizeOptions,
};

// Configure all i18n plugins in one place
i18n.use(initReactI18next);
i18n.use(LanguageDetector);
i18n.use(LocizeBackend);

// This helps identify unused keys that can be cleaned up
if (isDevelopmentEnvironment) {
  i18n.use(LastUsed); // tracks which translation keys are actually used
  i18nInitOptions.locizeLastUsed = locizeOptions;
  // i18nInitOptions.debug = true; // Enable this if you need to debug i18n stuff
}

export default i18n;
